/**
 * @file shared/desktop/DesktopLauncherBridge.tsx
 * @brief Tells the desktop app's menu-bar icon which quick actions this user
 *        may start, and opens the page when one is clicked.
 *
 * @details
 * DES 02-10-2026: the tray menu offers "Start a Boardroom", "Start
 * interpretation" and "New note". Only the page knows who is signed in, which
 * shell they use (`/app`, `/company`, `/admin`) and what their company allows,
 * so it sends the list with each item's path; the shell just shows it. The same
 * rules as the routes' guards decide: Boardroom and Notes entitlements, and
 * interpretation's entitlement AND the web pilot list. Signed out = no items.
 *
 * Since 02-10-2026 it also sends the app menu's Go items (DESKTOP/src/menu.js):
 * Home, Boardroom, Scheduled Meetings, Sessions, Notes, by the same rules as
 * the sidebar (employeeNavConfig), so nobody sees a page their shell or company
 * does not have.
 *
 * Renders nothing. Mounted once in App; skipped in the desktop app's own small
 * windows (widget, settings).
 *
 * @author DES
 * @date 02-10-2026
 */

import { useEffect } from "react";
import { useAuth } from "@/shared/auth/auth-context";
import { isCompanyAdminRole } from "@/shared/auth/homePath";
import { env } from "@/shared/config/env";
import { companyIdOf, webInterpretAllowed } from "@/features/interpret/pilot";
import { router } from "@/routes";
import { desktopShell } from "./desktopShell";

export type DesktopLauncher = { id: "boardroom" | "interpret" | "note"; label: string; path: string };
export type DesktopGoItem = { id: string; label: string; path: string };

export function DesktopLauncherBridge() {
  const { user, status, entitlements } = useAuth();
  const isWidget = window.location.pathname.startsWith("/desktop/");

  const items: DesktopLauncher[] = [];
  const go: DesktopGoItem[] = [];
  if (status === "authenticated" && user && !isWidget) {
    const superadmin = user.role === "superadmin";
    const base = superadmin ? "/admin" : isCompanyAdminRole(user.role) ? "/company" : "/app";
    if (!superadmin && entitlements.boardroom !== false) {
      items.push({ id: "boardroom", label: "Start a Boardroom", path: `${base}/meetings/new` });
    }
    if (
      !superadmin &&
      entitlements.interpretation !== false &&
      webInterpretAllowed(companyIdOf(user), env.webInterpretCompanies)
    ) {
      items.push({ id: "interpret", label: "Start interpretation", path: `${base}/interpret` });
    }
    if (entitlements.notes !== false) {
      items.push({ id: "note", label: "New note", path: `${base}/notes/new` });
    }

    if (superadmin) {
      go.push(
        { id: "home", label: "Dashboard", path: "/admin" },
        { id: "meetings", label: "Meetings", path: "/admin/meetings" },
        { id: "sessions", label: "Sessions", path: "/admin/sessions" },
      );
    } else {
      go.push({ id: "home", label: "Home", path: base === "/company" ? "/company/home" : "/app" });
      if (entitlements.boardroom !== false) {
        go.push(
          { id: "meetings", label: "Boardroom", path: `${base}/meetings` },
          { id: "scheduled", label: "Scheduled Meetings", path: `${base}/meetings/scheduled` },
        );
      }
      if (entitlements.interpretation !== false) {
        go.push({ id: "sessions", label: "Sessions", path: `${base}/sessions` });
      }
      if (entitlements.notes !== false) {
        go.push({ id: "notes", label: "Notes", path: `${base}/notes` });
      }
    }
  }
  const key = JSON.stringify(items);
  const goKey = JSON.stringify(go);

  useEffect(() => {
    const shell = desktopShell();
    if (isWidget || !shell?.setLaunchers) return;
    shell.setLaunchers(JSON.parse(key) as DesktopLauncher[]);
  }, [key, isWidget]);

  useEffect(() => {
    const shell = desktopShell();
    if (isWidget || !shell?.setNavigation) return;
    shell.setNavigation(JSON.parse(goKey) as DesktopGoItem[]);
  }, [goKey, isWidget]);

  useEffect(() => {
    const shell = desktopShell();
    if (isWidget || !shell?.onLaunch) return;
    return shell.onLaunch((path) => {
      // Only paths this page itself offered.
      const offered = [...(JSON.parse(key) as DesktopLauncher[]), ...(JSON.parse(goKey) as DesktopGoItem[])];
      if (offered.some((i) => i.path === path)) void router.navigate(path);
    });
  }, [key, goKey, isWidget]);

  return null;
}
